/** Ads per client (B02-001..003, B03-001..002): account mapping, optimisation queue, token health, GBP locations. */
import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { Button, Heading, Panel, Text } from '@qm/ui';
import { useSurface } from '../kit/context';
import { useAction, useResource } from '../kit/resource';
import { Badge, Checkbox, DataTable, ErrorNote, Field, Loadable, Money, Notice, Screen, Tabs, When } from '../kit/ui';
import type { ClientView } from '../api';
import { CONNECTION_TONE, accountMapState, groupByClient } from '../lib/logic';
import { usePartnerApi } from './layout';

type Provider = 'meta' | 'google_ads';

/** Map each ad account (or MCC customer ID) of the agency connection to exactly one client workspace. */
export function AccountMapping({ provider }: { provider: Provider }) {
  const { t } = useSurface();
  const api = usePartnerApi();
  const conns = useResource(`connections:${provider}`, () => api.connections(provider));
  const clients = useResource('clients:mapping', () => api.clients({ tab: 'active', limit: 100 }).then((e) => e.data));
  return (
    <Loadable resource={conns}>
      {(list) => list.length === 0 ? (
        <Notice testId="no-agency-connection">{t(`portal.partners.ads.${provider}.connectFirst`)}</Notice>
      ) : (
        <Loadable resource={clients}>
          {(cl) => <>{list.map((c) => <ConnectionAccounts key={c.id} connectionId={c.id} name={c.displayName ?? c.accountRef} clients={cl} />)}</>}
        </Loadable>
      )}
    </Loadable>
  );
}

function ConnectionAccounts({ connectionId, name, clients }: { connectionId: string; name: string; clients: ClientView[] }) {
  const { t } = useSurface();
  const api = usePartnerApi();
  const accounts = useResource(`accounts:${connectionId}`, () => api.connectionAccounts(connectionId));
  const [choice, setChoice] = useState<Record<string, string>>({});
  const map = useAction((accountRef: string, clientTenantId: string) => api.createMapping({ agencyConnectionId: connectionId, accountRef, clientTenantId }));
  return (
    <Panel>
      <Heading level={2}>{name}</Heading>
      <Loadable resource={accounts}>
        {(rows) => (
          <DataTable rows={rows} rowKey={(a) => a.ref} empty={t('portal.kit.empty')} columns={[
            { key: 'acct', header: t('portal.partners.ads.account'), render: (a) => a.name ?? a.ref },
            {
              key: 'state', header: t('portal.partners.ads.mapping'), render: (a) => {
                const st = accountMapState(a);
                if (st === 'mapped') return <Badge tone="good">{t('portal.partners.ads.mappedTo', { client: clients.find((c) => c.id === a.mappedTo)?.name ?? a.mappedTo ?? '' })}</Badge>;
                if (st === 'other_agency') return <Badge tone="bad" testId="map-other-agency">{t('portal.partners.ads.otherAgency')}</Badge>;
                if (st === 'consent_missing') return <Badge tone="warn" testId="map-consent-missing">{t('portal.partners.ads.consentMissing')}</Badge>;
                return (
                  <span className="qx-toolbar">
                    <select aria-label={t('portal.partners.ads.chooseClient')} value={choice[a.ref] ?? ''} onChange={(e) => setChoice((m) => ({ ...m, [a.ref]: e.target.value }))}>
                      <option value="">{t('portal.partners.ads.chooseClient')}</option>
                      {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                    <Button disabled={!choice[a.ref] || map.pending} onClick={async () => { if (await map.run(a.ref, choice[a.ref]!)) accounts.reload(); }}>{t('portal.partners.ads.map')}</Button>
                  </span>
                );
              },
            },
          ]} />
        )}
      </Loadable>
      <ErrorNote error={map.error} />
    </Panel>
  );
}

/** Cross-client optimisation queue: grouped per client; each group goes only to that client's approver. */
export function OptimisationQueue() {
  const { t } = useSurface();
  const api = usePartnerApi();
  const props = useResource('proposals:draft', () => api.proposals({ status: 'draft' }));
  const [sel, setSel] = useState<Set<string>>(new Set());
  const send = useAction(api.sendProposals);
  return (
    <Loadable resource={props}>
      {(rows) => (
        <div data-testid="optimisation-queue">
          {groupByClient(rows).map((g) => (
            <Panel key={g.clientId} data-client={g.clientId}>
              <Heading level={2}>{g.clientName}</Heading>
              <Text tone="muted">{t('portal.partners.ads.sentToApprover')}</Text>
              {g.rows.map((p) => (
                <div key={p.id} className="qx-card">
                  <Checkbox label={`${t(`portal.partners.ads.kind.${p.kind}`)} - ${p.campaignRef}`} checked={sel.has(p.id)} onChange={(v) => setSel((s) => { const n = new Set(s); if (v) n.add(p.id); else n.delete(p.id); return n; })} />
                  <Text tone="muted">{p.reason}</Text>
                  <span>{t('portal.partners.ads.delta')}: <Money minor={p.spendDeltaMinorPerDay} currency={p.currency} /></span>
                  {p.decision?.policyId ? <Badge tone="accent" testId="policy-approved">{t('portal.partners.ads.policyApproved')}</Badge> : null}
                </div>
              ))}
            </Panel>
          ))}
          {rows.length === 0 ? <Notice>{t('portal.partners.ads.queueEmpty')}</Notice> : null}
          <Button variant="primary" disabled={sel.size === 0 || send.pending} onClick={async () => { if (await send.run([...sel])) { setSel(new Set()); props.reload(); } }}>{t('portal.partners.ads.sendForApproval', { n: sel.size })}</Button>
          {send.result ? <Notice testId="send-result">{t('portal.partners.ads.sent', { n: send.result.sent, p: send.result.policyApproved })}</Notice> : null}
          <ErrorNote error={send.error} />
        </div>
      )}
    </Loadable>
  );
}

/** Per-client token health board with reconnect (B02-003). */
export function HealthBoard({ provider }: { provider: Provider }) {
  const { t } = useSurface();
  const api = usePartnerApi();
  const health = useResource(`health:${provider}`, () => api.health(provider));
  const reconnect = useAction(async (clientTenantId?: string) => {
    const r = await api.reconnect(provider, clientTenantId);
    if (typeof window !== 'undefined' && r.authorizeUrl.startsWith('https://')) window.location.assign(r.authorizeUrl);
    return r;
  });
  return (
    <Loadable resource={health}>
      {(rows) => (
        <>
          <DataTable rows={rows} rowKey={(r) => r.id} empty={t('portal.kit.empty')} columns={[
            { key: 'client', header: t('portal.partners.clients.col.name'), render: (r) => r.clientName ?? r.displayName ?? r.accountRef },
            { key: 'status', header: t('portal.partners.clients.col.status'), render: (r) => <Badge tone={CONNECTION_TONE[r.status] ?? 'neutral'} testId={`conn-${r.status}`}>{t(`portal.partners.conn.${r.status}`)}</Badge> },
            { key: 'flagged', header: t('portal.partners.ads.flaggedAt'), narrow: 'hide', render: (r) => <When iso={r.flaggedAt ?? null} /> },
            { key: 'sync', header: t('portal.partners.ads.lastSuccess'), narrow: 'hide', render: (r) => <When iso={r.lastSuccessAt} /> },
            { key: 'backfill', header: t('portal.partners.ads.backfill'), narrow: 'hide', render: (r) => (r.backfill && r.backfill !== 'none' ? t(`portal.partners.ads.backfill.${r.backfill}`) : '-') },
            { key: 'act', header: '', render: (r) => (r.action === 'reconnect' ? <Button onClick={() => void reconnect.run(r.clientTenantId ?? undefined)}>{t('portal.partners.ads.reconnect')}</Button> : null) },
          ]} />
          <ErrorNote error={reconnect.error} />
        </>
      )}
    </Loadable>
  );
}

export function MetaPage() {
  const { t } = useSurface();
  const [sp, setSp] = useSearchParams();
  const tab = sp.get('tab') ?? 'mapping';
  return (
    <Screen title={t('portal.partners.ads.meta.title')} testId="meta">
      <Tabs label={t('portal.partners.ads.meta.title')} active={tab} onSelect={(v) => setSp({ tab: v })}
        tabs={[{ id: 'mapping', label: t('portal.partners.ads.tab.mapping') }, { id: 'queue', label: t('portal.partners.ads.tab.queue') }, { id: 'health', label: t('portal.partners.ads.tab.health') }]} />
      {tab === 'queue' ? <OptimisationQueue /> : tab === 'health' ? <HealthBoard provider="meta" /> : <AccountMapping provider="meta" />}
    </Screen>
  );
}

export function GooglePage() {
  const { t } = useSurface();
  const [sp, setSp] = useSearchParams();
  const tab = sp.get('tab') ?? 'mapping';
  return (
    <Screen title={t('portal.partners.ads.google.title')} subtitle={t('portal.partners.ads.google.unmappedNote')} testId="google">
      <Tabs label={t('portal.partners.ads.google.title')} active={tab} onSelect={(v) => setSp({ tab: v })}
        tabs={[{ id: 'mapping', label: t('portal.partners.ads.tab.mapping') }, { id: 'health', label: t('portal.partners.ads.tab.health') }]} />
      {tab === 'health' ? <HealthBoard provider="google_ads" /> : <AccountMapping provider="google_ads" />}
    </Screen>
  );
}

/** All client GBP locations in one view with a client filter (B03-002). */
export function LocationsPage() {
  const { t } = useSurface();
  const api = usePartnerApi();
  const [client, setClient] = useState('');
  const locs = useResource(`gbp:${client}`, () => api.gbpLocations(client ? { client } : {}));
  return (
    <Screen title={t('portal.partners.locations.title')} testId="locations">
      <Loadable resource={locs}>
        {(rows) => {
          const clients = [...new Map(rows.map((r) => [r.clientTenantId, r.clientName ?? r.clientTenantId])).entries()];
          return (
            <>
              <Field label={t('portal.partners.locations.client')}>
                {(id) => <select id={id} value={client} onChange={(e) => setClient(e.target.value)}><option value="">{t('portal.partners.clients.any')}</option>{clients.map(([cid, n]) => <option key={cid} value={cid}>{n}</option>)}</select>}
              </Field>
              <DataTable rows={rows} rowKey={(r) => r.id} empty={t('portal.kit.empty')} columns={[
                { key: 'title', header: t('portal.partners.locations.location'), render: (r) => r.title },
                { key: 'client', header: t('portal.partners.locations.client'), render: (r) => r.clientName ?? r.clientTenantId },
                { key: 'status', header: t('portal.partners.clients.col.status'), render: (r) => <Badge tone={CONNECTION_TONE[r.status] ?? 'neutral'} testId={`loc-${r.status}`}>{t(`portal.partners.conn.${r.status}`)}</Badge> },
                { key: 'rating', header: t('portal.partners.locations.rating'), narrow: 'hide', render: (r) => (r.rating ?? '-') },
                { key: 'reviews', header: t('portal.partners.locations.newReviews'), narrow: 'hide', render: (r) => (r.newReviews ?? 0) },
              ]} />
            </>
          );
        }}
      </Loadable>
    </Screen>
  );
}
